import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import axios from 'axios';

// Public unsubscribe landing page. Linked from the footer of every
// outbound email with ?t=<tenantId>&e=<email>. No auth — the link
// itself carries enough info to suppress further sends.

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function UnsubscribePage() {
  const [params] = useSearchParams();
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');

  const tenantId = params.get('t') || '';
  const email = params.get('e') || '';

  useEffect(() => {
    if (!tenantId || !email) {
      setState('error');
      return;
    }
    axios
      .post(`${API_BASE_URL}/api/public/unsubscribe`, { tenantId, email })
      .then(() => setState('ok'))
      .catch(() => setState('error'));
  }, [tenantId, email]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {state === 'loading' && (
          <>
            <Loader2 className="h-10 w-10 text-[#6366F1] animate-spin mx-auto mb-4" />
            <p className="text-sm text-slate-500">Processing your request…</p>
          </>
        )}

        {state === 'ok' && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mb-5">
              <CheckCircle2 className="h-7 w-7 text-emerald-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">You've been unsubscribed</h1>
            <p className="text-sm text-slate-500 mb-5">
              <b>{email}</b> will no longer receive marketing or automation emails from this
              organization. Transactional messages (like password resets) may still be sent for
              account safety.
            </p>
            <p className="text-xs text-slate-400">
              Didn't mean to unsubscribe? Just reply to the email you received and the sender can
              re-add you.
            </p>
          </>
        )}

        {state === 'error' && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-amber-50 flex items-center justify-center mb-5">
              <AlertTriangle className="h-7 w-7 text-amber-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900 mb-2">Link not valid</h1>
            <p className="text-sm text-slate-500 mb-5">
              This unsubscribe link looks malformed or expired. If you keep receiving emails you
              don't want, reply to the sender directly and ask them to remove you.
            </p>
            <Link to="/" className="text-sm text-[#6366F1] hover:underline">
              Go to homepage
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
